/**
 * Pure Hilfsfunktionen rund um archivierte Angebots-Versionen.
 *
 * Beim Ändern eines abgeschlossenen Angebots wird der letzte Stand als
 * frühere Version eingefroren. Diese Funktionen bauen (a) den kompletten,
 * PDF-fähigen Freeze-Stand (inkl. Foto-Aufschlüsselungs-Positionen) und
 * (b) aus einer archivierten Version die Daten für den PDF-Export.
 *
 * Bewusst OHNE Import aus pdf-generator.ts gehalten (das Modul zieht jsPDF
 * und Bild-Assets), damit diese Logik in Node-Tests lauffähig ist. Die
 * Rückgabe von `buildVersionQuoteData` ist strukturell kompatibel zu
 * `QuoteData` aus pdf-generator.ts.
 */

import type { StoredQuoteVersion } from './storage';

export const QUOTE_TAX_RATE = 0.20; // 20% MwSt

/** Minimale Positions-Form, die zum Einfrieren benötigt wird. */
export interface FreezableItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  variant?: boolean;
  sourceLabel?: string;
}

/** Minimale Form eines Foto-Aufschlüsselungs-Eintrags fürs Einfrieren. */
export interface FreezablePerPhotoEntry {
  photoLabel: string;
  positions: Array<{
    name: string;
    qty: number;
    unit: string;
    ep: number;
    total: number;
    variant?: boolean;
  }>;
  subtotal: number;
}

export type FrozenQuoteItem = NonNullable<StoredQuoteVersion['frozenItems']>[number];

/**
 * Baut den kompletten Freeze-Stand eines Angebots: Hauptpositionen PLUS die
 * pro Foto aufgeschlüsselten Positionen. Wichtig, weil bei vorhandener
 * Foto-Aufschlüsselung die schadensbezogenen Positionen NICHT in der
 * Hauptliste (`mergedItems`) enthalten sind — ohne diese Flachlegung würde
 * eine archivierte Version die Foto-Positionen und deren Summenanteil
 * verlieren.
 */
export function buildFrozenQuoteState(
  mergedItems: FreezableItem[],
  perPhotoBreakdown: FreezablePerPhotoEntry[],
): { frozenItems: FrozenQuoteItem[]; frozenNetTotal: number } {
  const frozenItems: FrozenQuoteItem[] = mergedItems.map((it) => ({
    description: it.description,
    quantity: it.quantity,
    unit: it.unit,
    unitPrice: it.unitPrice,
    total: it.total,
    variant: it.variant ? true : undefined,
    sourceLabel: it.sourceLabel || undefined,
  }));
  let netTotal = mergedItems.reduce(
    (sum, it) => (it.variant ? sum : sum + it.total),
    0,
  );
  for (const entry of perPhotoBreakdown) {
    for (const pos of entry.positions) {
      frozenItems.push({
        description: pos.name,
        quantity: pos.qty,
        unit: pos.unit,
        unitPrice: pos.ep,
        total: pos.total,
        variant: pos.variant ? true : undefined,
        sourceLabel: entry.photoLabel,
      });
    }
    netTotal += entry.subtotal;
  }
  return { frozenItems, frozenNetTotal: netTotal };
}

/** PDF-fähige Angebotsdaten (strukturell kompatibel zu `QuoteData`). */
export interface VersionQuoteData {
  projectName: string;
  customerName: string;
  customerAddress: string;
  quoteNumber: string;
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
    variant?: boolean;
    sourceLabel?: string;
  }>;
  notes: string;
  validUntil: string;
  subtotal: number;
  tax: number;
  total: number;
  projectDetails?: StoredQuoteVersion['frozenProjectDetails'];
}

/**
 * Baut aus einer archivierten Version die PDF-Daten — ausschließlich aus den
 * eingefrorenen Feldern (Positionen, Nettosumme, Snapshot, Projektdetails).
 * `fallbackProjectName` wird nur verwendet, wenn die Version noch keinen
 * eingefrorenen Projektnamen trägt (ältere Archiv-Stände).
 */
export function buildVersionQuoteData(
  version: StoredQuoteVersion,
  fallbackProjectName: string,
): VersionQuoteData {
  const frozenItems = version.frozenItems ?? [];
  const snap = version.snapshot;
  const subtotal =
    typeof version.frozenNetTotal === 'number'
      ? version.frozenNetTotal
      : frozenItems.reduce((sum, it) => (it.variant ? sum : sum + it.total), 0);
  const tax = subtotal * QUOTE_TAX_RATE;
  return {
    projectName: version.frozenProjectName || fallbackProjectName || 'Bauprojekt',
    customerName: snap.customerData.name,
    customerAddress: snap.customerData.address,
    quoteNumber: version.quoteNumber,
    items: frozenItems.map((it) => ({
      description: it.description,
      quantity: it.quantity,
      unit: it.unit,
      unitPrice: it.unitPrice,
      total: it.total,
      ...(it.variant ? { variant: true } : {}),
      ...(it.sourceLabel ? { sourceLabel: it.sourceLabel } : {}),
    })),
    notes: snap.notes,
    validUntil: snap.validUntil,
    subtotal,
    tax,
    total: subtotal + tax,
    ...(version.frozenProjectDetails
      ? { projectDetails: version.frozenProjectDetails }
      : {}),
  };
}
